"use client"

import { HttpTypes } from "@medusajs/types"
import { Table, Text } from "@medusajs/ui"
import Markdown from "react-markdown"
import Accordion from "./accordion"

type ProductTabsProps = {
  product: HttpTypes.StoreProduct
}

const ProductTabs = ({ product }: ProductTabsProps) => {
  const tabs = [
    {
      label: "Opis",
      component: <ProductSpecsTab product={product} />,
    },
    {
      label: "Specifikacije",
      component: <ProductSpecificationsTab product={product} />,
    },
  ]

  return (
    <div className="w-full">
      <Accordion 
        type="multiple" 
        defaultValue={["Opis", "Specifikacije"]}
        className="flex flex-col gap-y-3 md:gap-y-4"
      >
        {tabs.map((tab, i) => (
          <Accordion.Item
            className="bg-white rounded-xl border border-neutral-200 px-4 md:px-6 lg:px-8"
            key={i}
            title={tab.label}
            headingSize="medium"
            value={tab.label}
          >
            {tab.component}
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  )
}

const ProductSpecsTab = ({ product }: ProductTabsProps) => {
  return (
    <div className="text-base md:text-lg py-4 md:py-6 lg:py-8 w-full">
      <Markdown
        components={{
          p: ({ children }) => (
            <Text className="text-neutral-950 mb-3 md:mb-4 leading-relaxed text-base md:text-lg">{children}</Text>
          ),
          h2: ({ children }) => (
            <Text className="text-lg md:text-xl lg:text-2xl text-neutral-950 my-3 md:my-4 lg:my-6 font-semibold">
              {children}
            </Text>
          ),
          h3: ({ children }) => (
            <Text className="text-base md:text-lg lg:text-xl text-neutral-950 mb-2 md:mb-3 font-semibold">{children}</Text>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 md:space-y-2 mb-3 md:mb-4 pl-2 md:pl-4">
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li className="text-neutral-950 leading-relaxed text-base md:text-lg">
              {children}
            </li>
          ),
        }}
      >
        {typeof product.description === "string" && product.description
          ? product.description
          : "-"}
      </Markdown>
    </div>
  )
}

const ProductSpecificationsTab = ({ product }: ProductTabsProps) => {
  // Internal metadata keys to exclude from customer-facing specifications
  const metadataKeysToExclude = [
    'krek_stock',
    'all_warehouse_stock',
    'has_other_warehouse_stock',
    'other_warehouse_delivery_days',
    'bestseller',
    'bestsellerStringValue',
    'pim_id',
  ]
   
  const specifications = [
    ...(product.weight ? [{ key: "Teža", value: `${product.weight} gramov` }] : []),
    ...(product.height || product.width || product.length ? [{
      key: "Dimenzije (VxŠxD)",
      value: `${product.height}mm x ${product.width}mm x ${product.length}mm`
    }] : []),
    ...(product.metadata ? Object.entries(product.metadata)
      .filter(([key]) => !metadataKeysToExclude.includes(key))
      // Only show primitive metadata values; object/array values (e.g. a `seo`
      // blob) are not renderable as a React child and must be skipped.
      .filter(([, value]) => value != null && typeof value !== 'object')
      .map(([key, value]) => ({
        key: key.replace(/_/g, ' ').replace(/^\w/, l => l.toUpperCase()),
        value: String(value)
      })) : [])
  ]

  return (
    <div className="text-base md:text-lg py-4 md:py-6 lg:py-8 w-full">
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
          <Table className="w-full">
            <Table.Body>
              {specifications.map((spec, index) => (
                <Table.Row key={index} className="border-b border-neutral-200 last:border-b-0">
                  <Table.Cell className="border-r border-neutral-200 px-4 py-3 lg:py-4">
                    <span className="font-semibold text-neutral-900 text-base md:text-lg">
                      {spec.key}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 lg:py-4 text-neutral-700">
                    <p className="break-words text-base md:text-lg">{spec.value}</p>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {specifications.map((spec, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-neutral-200 p-4 shadow-sm"
          >
            <div className="mb-2">
              <Text className="font-semibold text-neutral-900 text-base">
                {spec.key}
              </Text>
            </div>
            <div>
              <Text className="text-neutral-700 text-base leading-relaxed">
                {spec.value}
              </Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ProductTabs
